import React, { useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';

// Ensure a value is a valid number or default to provided value
const ensureNumber = (value, defaultValue = 0) => {
    return (value === undefined || value === null || isNaN(value)) ? defaultValue : value;
};

// Box plot configuration for different metrics
const BOXPLOT_CONFIGS = {
    activities: {
        title: 'Total Activities',
        colors: {
            fill: '#b3d1ff',
            stroke: '#4d94ff'
        },
        formatValue: (value) => Math.round(value),
        formatDisplay: (value) => Math.round(value).toString()
    },
    workingHours: {
        title: 'Total Working Hours',
        colors: {
            fill: '#b3e6cc',
            stroke: '#4da680'
        },
        formatValue: (value) => parseFloat(value),
        formatDisplay: (value) => value.toFixed(1)
    },
    artifacts: {
        title: 'Total Artifacts',
        colors: {
            fill: '#ffcc99',
            stroke: '#ff9933'
        },
        formatValue: (value) => Math.round(value),
        formatDisplay: (value) => Math.round(value).toString()
    }
};

const GenericBoxPlot = ({
    currentGroupValue,
    allGroupsValues,
    metric = 'activities'
}) => {
    const containerRef = useRef(null);
    const [showLegend, setShowLegend] = useState(false);
    

    useEffect(() => {
        if (!containerRef.current || !allGroupsValues || allGroupsValues.length === 0) return;

        

        const container = containerRef.current;
        if (!container || !allGroupsValues?.length) return;

        const observer = new ResizeObserver(entries => {
            const { width, height } = entries[0].contentRect;

            if (width > 0 && height > 0) {
                drawChart(width, height);
            }
        });

        observer.observe(container);

        return () => observer.disconnect();

    }, [currentGroupValue, allGroupsValues, metric]);

    const drawChart = (width, height) => {

        const config = BOXPLOT_CONFIGS[metric];

        if (!config) {
            console.warn(`Unknown metric: ${metric}`);
            return;
        }

        // Ensure currentGroupValue is a number
        const safeCurrentValue = ensureNumber(currentGroupValue, 0);
        
        // Ensure all values in allGroupsValues are numbers
        const safeValues = allGroupsValues.map(val => ensureNumber(val, 0)).filter(val => val >= 0);
        
        // If we have no valid data after filtering, return early
        if (safeValues.length === 0) {
            console.warn(`No valid ${metric} data to display`);
            return;
        }

        d3.select(containerRef.current).selectAll("svg").remove();
        const svg = d3.select(containerRef.current)
            .append("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("viewBox", `0 0 ${width} ${height}`)
            .attr("preserveAspectRatio", "xMidYMid meet");

        const margin = {
            top: height * 0.1, // Reduced top margin since title is now outside
            right: width * 0.05,
            bottom: height * 0.25, // Increased bottom margin for labels
            left: width * 0.05,
        };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        const g = svg.append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Add horizontal legend
        const legend = g.append("g")
            .attr("transform", `translate(80, 5)`);
        // Your Team
        legend.append("text")
            .attr("x", -80)
            .attr("y", 4)
            .attr("text-anchor", "start")
            .attr("fill", "#cc0000")
            .attr("font-weight", "bold")
            .attr("font-size", "12px")
            .text("Your Team: " + config.formatDisplay(safeCurrentValue));
        // Mean
        const mean = ensureNumber(d3.mean(safeValues), 0);
        legend.append("text")
            .attr("x", 40)
            .attr("y", 4)
            .attr("text-anchor", "start")
            .attr("fill", "#E91E63")
            .attr("font-weight", "bold")
            .attr("font-size", "12px")
            .text("Mean: " + config.formatDisplay(mean));

        let sortedData = [...safeValues].sort(d3.ascending);

        // Add padding values if needed for proper boxplot calculation
        if (sortedData.length < 5) {
            const paddedData = [...sortedData];
            const meanValue = mean || (metric === 'workingHours' ? 20 : metric === 'activities' ? 10 : 5);
            const standardDev = ensureNumber(d3.deviation(sortedData), metric === 'workingHours' ? 10 : metric === 'activities' ? 5 : 2);

            // Add synthetic values around mean to create a proper boxplot
            if (sortedData.length === 1) {
                paddedData.push(Math.max(0, meanValue - standardDev));
                paddedData.push(Math.max(0, meanValue - standardDev * 0.5));
                paddedData.push(Math.min(100, meanValue + standardDev * 0.5));
                paddedData.push(Math.min(100, meanValue + standardDev));
            } else if (sortedData.length < 5) {
                // Add just enough synthetic values
                while (paddedData.length < 5) {
                    const next = paddedData.length % 2 === 0 ?
                        Math.max(0, meanValue - standardDev * (paddedData.length / 10)) :
                        Math.min(100, meanValue + standardDev * (paddedData.length / 10));
                    paddedData.push(next);
                }
            }
            sortedData = paddedData.sort(d3.ascending);
        }

        // Ensure all quartile values are valid numbers
        const q1 = ensureNumber(d3.quantile(sortedData, 0.25), sortedData[0]);
        const median = ensureNumber(d3.quantile(sortedData, 0.5), mean);
        const q3 = ensureNumber(d3.quantile(sortedData, 0.75), sortedData[sortedData.length - 1]);
        const iqr = ensureNumber(q3 - q1, 1);
        const min = Math.max(0, ensureNumber(q1 - 1.5 * iqr, 0));
        const max = ensureNumber(q3 + 1.5 * iqr, Math.max(...sortedData, safeCurrentValue));

        // Ensure the domain has a reasonable minimum range
        const maxValue = Math.max(max, safeCurrentValue, ...safeValues);
        const minRange = Math.max(maxValue / 10, metric === 'workingHours' ? 5 : metric === 'activities' ? 5 : 1);

        const xScale = d3.scaleLinear()
            .domain([0, maxValue + (metric === 'artifacts' ? 1 : minRange)])
            .range([0, chartWidth])
            .nice();

        // Box rect - Ensure width is positive and x position is valid
        const boxX = xScale(q1);
        const boxWidth = Math.max(0, xScale(q3) - xScale(q1));

        if (!isNaN(boxX) && !isNaN(boxWidth)) {
            g.append("rect")
                .attr("x", boxX)
                .attr("y", chartHeight / 2 - 15)
                .attr("width", boxWidth)
                .attr("height", 30)
                .attr("fill", config.colors.fill)
                .attr("stroke", config.colors.stroke)
                .attr("stroke-width", 1);
        }

        // Median line - Ensure x position is valid
        const medianX = xScale(median);
        if (!isNaN(medianX)) {
            g.append("line")
                .attr("x1", medianX)
                .attr("x2", medianX)
                .attr("y1", chartHeight / 2 - 15)
                .attr("y2", chartHeight / 2 + 15)
                .attr("stroke", "#000")
                .attr("stroke-width", 2);
        }

        // Mean line - Ensure x position is valid
        const meanX = xScale(mean);
        if (!isNaN(meanX)) {
            g.append("line")
                .attr("x1", meanX)
                .attr("x2", meanX)
                .attr("y1", chartHeight / 2 - 15)
                .attr("y2", chartHeight / 2 + 15)
                .attr("stroke", "#E91E63")
                .attr("stroke-width", 2)
                .attr("stroke-dasharray", "2");
        }

        // Min to Q1 line - Ensure both x positions are valid
        const minX = xScale(min);
        if (!isNaN(minX) && !isNaN(boxX)) {
            g.append("line")
                .attr("x1", minX)
                .attr("x2", boxX)
                .attr("y1", chartHeight / 2)
                .attr("y2", chartHeight / 2)
                .attr("stroke", config.colors.stroke)
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "4");
        }

        // Q3 to Max line - Ensure both x positions are valid
        const maxX = xScale(max);
        const q3X = xScale(q3);
        if (!isNaN(q3X) && !isNaN(maxX)) {
            g.append("line")
                .attr("x1", q3X)
                .attr("x2", maxX)
                .attr("y1", chartHeight / 2)
                .attr("y2", chartHeight / 2)
                .attr("stroke", config.colors.stroke)
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "4");
        }

        // Min tick - Ensure x position is valid
        if (!isNaN(minX)) {
            g.append("line")
                .attr("x1", minX)
                .attr("x2", minX)
                .attr("y1", chartHeight / 2 - 5)
                .attr("y2", chartHeight / 2 + 5)
                .attr("stroke", config.colors.stroke)
                .attr("stroke-width", 1);
        }

        // Max tick - Ensure x position is valid
        if (!isNaN(maxX)) {
            g.append("line")
                .attr("x1", maxX)
                .attr("x2", maxX)
                .attr("y1", chartHeight / 2 - 5)
                .attr("y2", chartHeight / 2 + 5)
                .attr("stroke", config.colors.stroke)
                .attr("stroke-width", 1);
        }

        // Q3 tick - Ensure x position is valid
        if (!isNaN(q3X)) {
            g.append("line")
                .attr("x1", q3X)
                .attr("x2", q3X)
                .attr("y1", chartHeight / 2 - 5)
                .attr("y2", chartHeight / 2 + 5)
                .attr("stroke", config.colors.stroke)
                .attr("stroke-width", 2);
        }

        // Current team marker - Ensure x position is valid
        const currentX = xScale(safeCurrentValue);
        if (!isNaN(currentX)) {
            g.append("circle")
                .attr("cx", currentX)
                .attr("cy", chartHeight / 2)
                .attr("r", 8)
                .attr("fill", "#ff5c33")
                .attr("stroke", "#cc0000")
                .attr("stroke-width", 1);
        }

        // Axis
        const axisG = g.append("g")
            .attr("transform", `translate(0,${chartHeight - 15})`);

        const keyValues = [min, q1, median, mean, q3, max].filter(val => !isNaN(val));
        keyValues.sort((a, b) => a - b);

        axisG.append("line")
            .attr("x1", 0)
            .attr("x2", chartWidth)
            .attr("y1", 0)
            .attr("y2", 0)
            .attr("stroke", "#000")
            .attr("stroke-width", 1);

        const labels = ["Min", "Q1", "Med", "Mean", "Q3", "Max"];

        const usedPositions = new Map();

        keyValues.forEach((value, i) => {
            if (isNaN(value)) return;

            const xPos = xScale(value);
            if (isNaN(xPos)) return;

            const label = i < labels.length ? labels[i] : `Val ${i}`;

            if (label === "Med" || label === "Q3" || !usedPositions.has(label)) {
                let tooClose = false;

                if (label !== "Med" && label !== "Q3") {
                    usedPositions.forEach((pos) => {
                        if (Math.abs(xPos - pos) < 20) tooClose = true;
                    });
                }

                if (!tooClose || label === "Med" || label === "Q3") {
                    axisG.append("line")
                        .attr("x1", xPos)
                        .attr("x2", xPos)
                        .attr("y1", 0)
                        .attr("y2", 5)
                        .attr("stroke", "#000")
                        .attr("stroke-width", 1);

                    const yOffset = i % 2 === 0 ? 15 : 25;

                    axisG.append("text")
                        .attr("x", xPos)
                        .attr("y", yOffset)
                        .attr("text-anchor", "middle")
                        .attr("fill", "#666")
                        .attr("font-size", "10px")
                        .text(label);

                    axisG.append("text")
                        .attr("x", xPos)
                        .attr("y", yOffset + 12)
                        .attr("text-anchor", "middle")
                        .attr("fill", "#666")
                        .attr("font-size", "10px")
                        .text(config.formatDisplay(value));

                    usedPositions.set(label, xPos);
                }
            }
        });
    };


    const config = BOXPLOT_CONFIGS[metric] || BOXPLOT_CONFIGS.activities;

    return (
        <ChartWrapper>
            <TitleContainer>
                <Title>
                    {config.title}
                    <InfoIconContainer
                        onMouseEnter={() => setShowLegend(true)}
                        onMouseLeave={() => setShowLegend(false)}
                    >
                        <InfoIcon>ⓘ</InfoIcon>
                        {showLegend && (
                            <LegendTooltip metric={metric}>
                                <LegendTitle>Box Plot Legend</LegendTitle>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '30px',
                                            height: '16px',
                                            backgroundColor: config.colors.fill,
                                            border: `2px solid ${config.colors.stroke}`,
                                            borderRadius: '2px',
                                            position: 'relative'
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                left: '50%',
                                                top: '0',
                                                bottom: '0',
                                                width: '2px',
                                                backgroundColor: '#000',
                                                transform: 'translateX(-50%)'
                                            }}></div>
                                        </div>
                                    </LegendSymbol>
                                    <LegendText>Box: Q1 to Q3 range with median line (black)</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '30px',
                                            height: '4px',
                                            backgroundColor: config.colors.stroke,
                                            borderRadius: '2px',
                                            border: '1px dashed #666'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>Whiskers: Extend to min/max within 1.5×IQR</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '30px',
                                            height: '2px',
                                            backgroundColor: 'transparent',
                                            borderTop: '2px dotted #E91E63',
                                            borderRadius: '1px'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>Dotted pink line: Course mean value</LegendText>
                                </LegendItem>
                                <LegendItem>
                                    <LegendSymbol>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            backgroundColor: '#ff5c33',
                                            border: '2px solid #cc0000',
                                            borderRadius: '50%'
                                        }}></div>
                                    </LegendSymbol>
                                    <LegendText>Red circle: Your team's current value</LegendText>
                                </LegendItem>
                            </LegendTooltip>
                        )}
                    </InfoIconContainer>
                </Title>
            </TitleContainer>
            <ChartContainer ref={containerRef} />
        </ChartWrapper>
    );
};

const ChartWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0px;
    position: relative;
    flex-shrink: 0;
    height: 30px;
`;

const Title = styled.h3`
    font-size: 16px;
    margin: 0;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const InfoIconContainer = styled.div`
    position: relative;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const InfoIcon = styled.div`
    font-size: 14px;
    color: #666;
    background-color: #f5f5f5;
    border: 1px solid #ccc;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #e0e0e0;
        border-color: #999;
        color: #333;
    }
`;

const LegendTooltip = styled.div`
    position: absolute;
    top: 25px;
    left: ${props => (props.metric === 'workingHours' || props.metric === 'artifacts') ? '-280px' : '-140px'};
    background-color: #fff;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 15px;
    width: 320px;
    z-index: 1000;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const LegendTitle = styled.h4`
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 12px 0;
    color: #333;
    text-align: center;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    &:last-child {
        margin-bottom: 0;
    }
`;

const LegendSymbol = styled.div`
    width: 30px;
    height: 20px;
    margin-right: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LegendText = styled.div`
    font-size: 12px;
    color: #555;
    line-height: 1.3;
`;

const ChartContainer = styled.div`
    width: 100%;
    height: 100%;
    min-height: 100px;
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export default React.memo(GenericBoxPlot); 